import React, { useState } from 'react';
import { IoSave } from 'react-icons/io5';
import { University } from '@/types';
import ConfirmationModal from '@/components/ConfirmationModal';

interface UniversityDetailsFormProps {
  university: University;
  formData: Partial<University>;
  onInputChange: (field: keyof University, value: any) => void;
  onSave: () => void;
  onDelete?: (universityId: number) => void;
  saving: boolean;
}

const UniversityDetailsForm: React.FC<UniversityDetailsFormProps> = ({
  university,
  formData,
  onInputChange,
  onSave,
  onDelete,
  saving,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteModal(false);
    if (onDelete) {
      onDelete(university.id);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };
  return (
    <div className="bg-white rounded-lg shadow-lg">
      <div className="p-6">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">University Details</h3>
            <div className="flex items-center space-x-3">
              {onDelete && (
                <button
                  onClick={handleDeleteClick}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Delete University</span>
                </button>
              )}
              <button
                onClick={onSave}
                disabled={saving}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {IoSave({ className: "text-lg" })}
                <span>{saving ? 'Saving...' : 'Save All Changes'}</span>
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => onInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                value={formData.location || ''}
                onChange={(e) => onInputChange('location', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
              <select
                value={formData.environment || ''}
                onChange={(e) => onInputChange('environment', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="Urban">Urban</option>
                <option value="Suburban">Suburban</option>
                <option value="Rural">Rural</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Undergraduate Students</label>
              <input
                type="number"
                value={formData.undergrad_students || ''}
                onChange={(e) => onInputChange('undergrad_students', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Acceptance Rate (%)</label>
              <input
                type="number"
                step="0.1"
                value={formData.acceptance_rate || ''}
                onChange={(e) => onInputChange('acceptance_rate', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Requires SAT/ACT</label>
              <select
                value={formData.sat_act_status || ''}
                onChange={(e) => onInputChange('sat_act_status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="Required">Required</option>
                <option value="Optional">Optional</option>
                <option value="Not Required">Not Required</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Requires GPA</label>
              <select
                value={formData.high_school_gpa || ''}
                onChange={(e) => onInputChange('high_school_gpa', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Application Fee ($)</label>
              <input
                type="number"
                value={formData.application_fee || ''}
                onChange={(e) => onInputChange('application_fee', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Early Decision</label>
              <select
                value={formData.early_decision_early_action || ''}
                onChange={(e) => onInputChange('early_decision_early_action', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Early Decision Deadline
              </label>
              <input
                type="date"
                value={formData.application_deadline || ''}
                onChange={(e) => onInputChange('application_deadline', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Application Deadline</label>
              <input
                type="date"
                value={formData.last_date_to_apply || ''}
                onChange={(e) => onInputChange('last_date_to_apply', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Net Price per Year ($)</label>
              <input
                type="number"
                value={formData.net_price_per_year || ''}
                onChange={(e) => onInputChange('net_price_per_year', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Housing Price ($)</label>
              <input
                type="number"
                value={formData.average_housing_cost || ''}
                onChange={(e) => onInputChange('average_housing_cost', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meal Plan Price ($)</label>
              <input
                type="number"
                value={formData.average_meal_plan_cost || ''}
                onChange={(e) => onInputChange('average_meal_plan_cost', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Books & Supplies ($)</label>
              <input
                type="number"
                value={formData.books_and_supplies || ''}
                onChange={(e) => onInputChange('books_and_supplies', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">In State Tuition ($)</label>
              <input
                type="number"
                value={formData.in_state_tuition || ''}
                onChange={(e) => onInputChange('in_state_tuition', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Out of State Tuition ($)</label>
              <input
                type="number"
                value={formData.out_of_state_tuition || ''}
                onChange={(e) => onInputChange('out_of_state_tuition', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Average Financial Aid ($)</label>
              <input
                type="number"
                value={formData.average_total_aid_awarded || ''}
                onChange={(e) => onInputChange('average_total_aid_awarded', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Students Receiving Aid (%)</label>
              <input
                type="number"
                value={formData.students_receiving_financial_aid || ''}
                onChange={(e) => onInputChange('students_receiving_financial_aid', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Student-Faculty Ratio</label>
              <input
                type="text"
                value={formData.student_faculty_ratio || ''}
                onChange={(e) => onInputChange('student_faculty_ratio', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Offers Online Courses</label>
              <select
                value={formData.offers_online_courses || ''}
                onChange={(e) => onInputChange('offers_online_courses', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Median Salary 5 Years ($)</label>
              <input
                type="number"
                value={formData.median_earnings_6_years_after_graduation || ''}
                onChange={(e) => onInputChange('median_earnings_6_years_after_graduation', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Rate (%)</label>
              <input
                type="number"
                step="0.1"
                value={formData.graduation_rate || ''}
                onChange={(e) => onInputChange('graduation_rate', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employed After 1 Year (%)</label>
              <input
                type="number"
                step="0.1"
                value={formData.employed_2_years_after_graduation || ''}
                onChange={(e) => onInputChange('employed_2_years_after_graduation', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete University"
        message="Are you sure you want to delete this university? This will also delete all supplements, prompts, and user associations."
        confirmText="OK"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmButtonColor="red"
      />
    </div>
  );
};

export default UniversityDetailsForm;